(function () {
  if (window.__multiContentSectionLoaded) return;
  window.__multiContentSectionLoaded = true;

class MultiContentSection {
  static MOBILE_MQ = '(max-width: 767px)';

  static boot() {
    if (typeof Swiper === 'undefined') {
      window.setTimeout(() => MultiContentSection.boot(), 50);
      return;
    }
    MultiContentSection.initAll();
  }

  static initAll() {
    document.querySelectorAll('[data-multi-content]').forEach((root) => {
      MultiContentSection._whenNear(root, () => {
        if (root.multiContentInstance) {
          root.multiContentInstance.destroy();
        }
        root.multiContentInstance = new MultiContentSection(root);
      });
    });
  }

  // Lazy-init: build the carousel only when the section nears the viewport, so
  // below-the-fold Swipers don't run on initial page load (cuts JS exec / TBT).
  // In the theme editor (designMode) init immediately for live preview.
  static _whenNear(el, cb) {
    if (window.Shopify && window.Shopify.designMode) { cb(); return; }
    if (el.dataset.lazyInitObserved === 'true') return;
    el.dataset.lazyInitObserved = 'true';
    if (!('IntersectionObserver' in window)) { cb(); return; }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { obs.unobserve(entry.target); cb(); }
      });
    }, { rootMargin: '300px 0px' });
    io.observe(el);
  }

  constructor(root) {
    this.root = root;
    this.track = root.querySelector('.bls__multi-content__track');
    this.mq = window.matchMedia(MultiContentSection.MOBILE_MQ);
    this.onMqChange = () => this.toggleMobileMode();

    if (!this.track) return;

    this.readConfig();
    this.mq.addEventListener('change', this.onMqChange);
    this.toggleMobileMode();
  }

  readConfig() {
    const root = this.root;
    const slidesRaw =
      root.dataset.slidesPerView ||
      root.dataset.mobileSlidesPerView ||
      root.dataset.mobileItems ||
      '1';
    const parsed = parseFloat(slidesRaw, 10);
    this.mobileItems = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    let gapMobile = parseInt(root.dataset.gapMobile, 10);
    if (!Number.isFinite(gapMobile) && this.track) {
      gapMobile = parseInt(
        getComputedStyle(this.track).getPropertyValue('--mc-gap-mobile'),
        10
      );
    }
    this.gapMobile = Math.max(0, Number.isFinite(gapMobile) ? gapMobile : 0);
    this.spaceBetween = this.gapMobile;
    this.mobileLayout = root.dataset.mobileLayout || 'grid';
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    let autoplayDelay = parseInt(root.dataset.autoplayDelay, 10);
    if (!Number.isFinite(autoplayDelay) || autoplayDelay < 1000) {
      autoplayDelay = 5000;
    }
    this.autoplayDelay = autoplayDelay;
    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  }

  isMobileViewport() {
    return this.mq.matches;
  }

  shouldUseSwiper() {
    return this.mobileLayout === 'swiper' && this.isMobileViewport();
  }

  getSlides() {
    if (!this.track) return [];
    return [...this.track.children].filter(
      (el) =>
        el.classList.contains('shopify-block') ||
        el.classList.contains('bls__multi-content__group')
    );
  }

  clearSlideClasses() {
    this.getSlides().forEach((slide) => {
      slide.classList.remove(
        'swiper-slide',
        'swiper-slide-active',
        'swiper-slide-next',
        'swiper-slide-prev',
        'swiper-slide-visible'
      );
    });
  }

  resetDesktopLayout() {
    this.root.classList.remove(
      'swiper',
      'bls__swiper',
      'bls__multi-content__items--mobile-swiper'
    );
    this.root.removeAttribute('style');

    if (!this.track) return;

    this.track.classList.remove('swiper-wrapper');
    this.track.removeAttribute('style');

    this.clearSlideClasses();
    this.getSlides().forEach((slide) => {
      slide.removeAttribute('style');
    });
  }

  destroy() {
    this.mq.removeEventListener('change', this.onMqChange);
    this.teardownSwiper();
    this.root.multiContentInstance = null;
  }

  destroySwiper() {
    if (!this.swiper) return;
    try {
      this.swiper.destroy(true, true);
    } catch (error) {
      /* Swiper may already be torn down */
    }
    this.swiper = null;
  }

  teardownSwiper() {
    this.destroySwiper();
    this.resetDesktopLayout();
  }

  toggleMobileMode() {
    this.readConfig();
    this.teardownSwiper();

    if (!this.shouldUseSwiper()) return;

    const slides = this.getSlides();
    if (!slides.length) return;

    this.root.classList.add(
      'swiper',
      'bls__swiper',
      'bls__multi-content__items--mobile-swiper'
    );
    this.track.classList.add('swiper-wrapper');
    slides.forEach((slide) => {
      slide.classList.add('swiper-slide');
    });

    const paginationEl = this.root.querySelector('.swiper-pagination');
    const nextEl = this.root.querySelector('.swiper-button-next');
    const prevEl = this.root.querySelector('.swiper-button-prev');
    const isSingleSlideView = this.mobileItems === 1;

    const swiperConfig = {
      wrapperClass: 'bls__multi-content__track',
      slideClass: 'swiper-slide',
      slidesPerView: this.mobileItems,
      spaceBetween: this.spaceBetween,
      slidesPerGroup: 1,
      centeredSlides: !isSingleSlideView && this.mobileItems < 2,
      centeredSlidesBounds: true,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      roundLengths: false,
      speed: 400,
      grabCursor: true,
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      threshold: 8,
      touchStartPreventDefault: false,
      preventClicks: false,
      preventClicksPropagation: false,
      watchOverflow: true,
      resistanceRatio: 0.85,
      observer: true,
      observeParents: true,
      watchSlidesProgress: true,
      on: {
        init: (swiper) => MultiContentSection.syncSwiperLayout(swiper),
        resize: (swiper) => MultiContentSection.syncSwiperLayout(swiper),
        breakpoint: (swiper) => MultiContentSection.syncSwiperLayout(swiper),
        slideChangeTransitionEnd: (swiper) =>
          MultiContentSection.syncSwiperLayout(swiper),
      },
    };

    if (paginationEl && slides.length > 1) {
      swiperConfig.pagination = {
        el: paginationEl,
        clickable: true,
        type: 'bullets',
      };
    }

    if (nextEl && prevEl && slides.length > 1) {
      swiperConfig.navigation = {
        nextEl,
        prevEl,
      };
    }

    if (
      this.autoplayEnabled &&
      !this.reducedMotion &&
      slides.length > 1
    ) {
      swiperConfig.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    this.swiper = new Swiper(this.root, swiperConfig);
    MultiContentSection.syncSwiperLayout(this.swiper);
    requestAnimationFrame(() => MultiContentSection.syncSwiperLayout(this.swiper));
  }

  static getUniformSlideWidth(swiper) {
    const slidesPerView = Number(swiper.params.slidesPerView) || 1;
    const spaceBetween = Number(swiper.params.spaceBetween) || 0;
    const containerWidth = swiper.width;

    if (!containerWidth || containerWidth <= 0) return 0;

    if (slidesPerView <= 1) {
      return containerWidth;
    }

    return (containerWidth - spaceBetween * (slidesPerView - 1)) / slidesPerView;
  }

  static applyUniformSlideWidths(swiper) {
    if (!swiper?.slides?.length) return;

    const slideWidth = MultiContentSection.getUniformSlideWidth(swiper);
    if (slideWidth <= 0) return;

    swiper.slides.forEach((slide) => {
      slide.style.width = `${slideWidth}px`;
      slide.style.flexBasis = `${slideWidth}px`;
      slide.style.flexShrink = '0';
    });
  }

  static syncSwiperLayout(swiper) {
    if (!swiper || swiper.destroyed) return;

    try {
      swiper.updateSize();
      MultiContentSection.applyUniformSlideWidths(swiper);
      swiper.updateSlides();
      swiper.updateProgress();
      swiper.updateSlidesClasses();

      if (swiper.pagination) {
        swiper.pagination.render();
        swiper.pagination.update();
      }

      if (swiper.navigation) {
        swiper.navigation.update();
      }
    } catch (error) {
      /* Swiper may be mid-teardown */
    }
  }
}

MultiContentSection.boot();

function initMultiContentInSection(section) {
  if (!section) return;
  section.querySelectorAll('[data-multi-content]').forEach((root) => {
    if (root.multiContentInstance) {
      root.multiContentInstance.destroy();
    }
    root.multiContentInstance = new MultiContentSection(root);
  });
}

document.addEventListener('shopify:section:load', (event) => {
  initMultiContentInSection(event.target);
});

document.addEventListener('shopify:section:reorder', (event) => {
  initMultiContentInSection(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const section = event.target.closest('.shopify-section');
  initMultiContentInSection(section);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-multi-content]').forEach((root) => {
    if (root.multiContentInstance) {
      root.multiContentInstance.destroy();
    }
  });
});
})();